import { Spinner, theme } from '@zappyrent/hundop';
import { upperFirst } from 'lodash';
import { rgba } from 'polished';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Media from 'react-media';
import { useNavigate, useParams } from 'react-router-dom';

import Filter from '../../components/Filter';
import ListingCard from '../../components/Listings/ListingCard';
import { MainLayout } from '../../components/MainLayout';
import listingsAPI from '../../services/api/listings';
import { useUser } from '../../shared/hooks';
import useUserDocuments from '../../shared/hooks/useUserDocuments';
import { IListing } from '../../shared/ts/listing';
import useStore from '../../store';
import {
  StyledAddIcon,
  StyledAddCtaButton,
  StyledListings,
  StyledTitle,
  StyledTopBar,
  StyledShowMoreCtaButton,
  StyledTitleWrapper,
} from './Listings.styles';

const Listings = (): JSX.Element | null => {
  const user = useStore(state => state.user);
  const userType = user?.type;
  const { t } = useTranslation(['listings', 'routes']);
  const navigate = useNavigate();
  const { locale } = useParams();

  const [listings, setListings] = useState<IListing[] | null>(null);
  const [listingsStatusFilters, setListingsStatusFilters] = useState<string[]>(
    [],
  );
  const [listingsPage, setListingsPage] = useState(1);
  const [isShowMoreBtnVisible, setIsShowMoreBtnVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const isLoadingUser = useUser();
  const isLoadingUserDocuments = useUserDocuments(user?.id ?? null);

  const getListingsFromApi = async (page = 1, replaceResults = true) => {
    try {
      setIsLoading(true);
      const {
        data: { data, total },
      } = await listingsAPI.getListings(listingsStatusFilters, page);

      const newListings =
        listings && !replaceResults ? [...listings, ...data] : data;
      setListings(newListings);

      if (listings && total === newListings.length) {
        setIsShowMoreBtnVisible(false);
        setListingsPage(1);
      } else if (!listings && total === newListings.length) {
        setIsShowMoreBtnVisible(false);
      } else {
        setIsShowMoreBtnVisible(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActiveListingsFilterChange = (newActiveOptions: string[]) => {
    setListingsStatusFilters(newActiveOptions);
  };

  const handleShowMoreBtnClick = async (page: number) => {
    getListingsFromApi(page, false);
    setListingsPage(page);
  };

  useEffect(() => {
    getListingsFromApi();
  }, [listingsStatusFilters]);

  if (isLoadingUser || isLoadingUserDocuments) return null;

  if (userType !== 'landlord') navigate(`/${locale}/${t('routes:profile')}`);

  return (
    <MainLayout>
      <StyledTopBar>
        <StyledTitleWrapper>
          <StyledTitle>{upperFirst(t('listings:listings'))}</StyledTitle>
          {isLoading && (
            <Spinner
              backgroundColor={theme.colors.cross.lightGray}
              foregroundColor={theme.colors.landlord.main}
            />
          )}
        </StyledTitleWrapper>
        <Media
          queries={{
            mobile: `(max-width: calc(${theme.breakpoints.m} - 1px))`,
            desktop: `(min-width: ${theme.breakpoints.m})`,
          }}
        >
          {matches =>
            matches && (
              <StyledAddCtaButton
                user={userType}
                variant="primary"
                tag="a"
                href={`${process.env.REACT_APP_SITE_URL}/user/dashboard/addProperty`}
              >
                {matches.desktop ? (
                  upperFirst(t('listings:createListing'))
                ) : (
                  <StyledAddIcon />
                )}
              </StyledAddCtaButton>
            )
          }
        </Media>
      </StyledTopBar>
      <Filter
        activeOptions={listingsStatusFilters}
        options={[
          { name: 'active', label: upperFirst(t('listings:active')) },
          { name: 'draft', label: upperFirst(t('listings:draft')) },
          {
            name: 'pending',
            label: upperFirst(t('listings:pending')),
          },
          {
            name: 'deleted',
            label: upperFirst(t('listings:deleted')),
          },
        ]}
        onChange={
          handleActiveListingsFilterChange as (x: string | string[]) => void
        }
        isMulti
      />
      <StyledListings>
        {listings &&
          listings.map((listing, index) => (
            <ListingCard
              key={index}
              id={listing.id}
              name={listing.name ?? listing.address}
              status={listing.status}
              price={listing.price}
              size={listing.size}
              nBedrooms={listing.total_bedrooms}
              nBathrooms={listing.bathrooms}
              image={listing.img_desktop_search}
              province={listing.province}
            />
          ))}
      </StyledListings>
      {isShowMoreBtnVisible && (
        <StyledShowMoreCtaButton
          user={userType}
          variant="primary"
          tag="button"
          onClick={handleShowMoreBtnClick.bind(null, listingsPage + 1)}
        >
          {isLoading && (
            <Spinner
              backgroundColor={rgba(theme.colors.main, 0.2)}
              foregroundColor={theme.colors.main}
            />
          )}
          {upperFirst(t('listings:showMore'))}
        </StyledShowMoreCtaButton>
      )}
    </MainLayout>
  );
};

export default Listings;
